import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import type { AppState } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router/dom';
import './styles/global.css';
import { API_AUDIENCE, auth0 } from './auth/auth0Client';
import { setTokenProvider } from './auth/tokenSource';
import { queryClient } from './app/queryClient';
import { router } from './app/router';
import { ThemeProvider } from './theme/ThemeProvider';
import { ToastProvider } from './ui/Toast';

// The one place the fetch layer learns where tokens come from. The audience is passed
// on every call: without it Auth0 issues an opaque token and every request 401s.
setTokenProvider(() => auth0.getTokenSilently({ authorizationParams: { audience: API_AUDIENCE } }));

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

/**
 * The same Auth0Client instance is handed to the provider that the fetch middleware
 * already holds. Letting auth0-react construct its own would give the app two token
 * caches, and the one that answered a 401 would not be the one React re-rendered from.
 */
createRoot(root).render(
  <StrictMode>
    <Auth0Provider
      client={auth0}
      onRedirectCallback={(appState?: AppState) => {
        // Navigate through the router rather than window.history, so React Router's own
        // state matches the URL. `replace` keeps the ?code= URL out of the back stack.
        void router.navigate(appState?.returnTo ?? '/overview', { replace: true });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          {/* Above the router: a toast raised by a route that then navigates away, or
              by a component that the change it reports removes — Delete now and Restore
              are both — must outlive the screen that raised it. */}
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Auth0Provider>
  </StrictMode>,
);
